import { useCallback, useEffect, useMemo, useState } from 'react';
import FidesChatAdminService, { ChatMessage } from '../../services/admin/FidesChatAdminService';
import { AdminChatAssistResult } from '../../models/admin/AdminChatAssistResult';

export type ChatAiAssistPanelMode = 'closed' | 'open' | 'minimized';

interface UseChatAiAssistOptions {
  conversationId: string | null;
  lastInvestorMessage: ChatMessage | null;
  disabled?: boolean;
}

function sortHistoryChronological(history: AdminChatAssistResult[]): AdminChatAssistResult[] {
  return [...history].sort((left, right) => left.createdAt.localeCompare(right.createdAt));
}

function upsertHistoryEntry(
  history: AdminChatAssistResult[],
  entry: AdminChatAssistResult,
): AdminChatAssistResult[] {
  const without = history.filter((item) => item.targetMessageId !== entry.targetMessageId);
  return sortHistoryChronological([...without, entry]);
}

export function useChatAiAssist({
  conversationId,
  lastInvestorMessage,
  disabled = false,
}: UseChatAiAssistOptions) {
  const [mode, setMode] = useState<ChatAiAssistPanelMode>('closed');
  const [history, setHistory] = useState<AdminChatAssistResult[]>([]);
  const [expandedTargetId, setExpandedTargetId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [followUpDraft, setFollowUpDraft] = useState('');
  const [followUpLoadingId, setFollowUpLoadingId] = useState<string | null>(null);
  const [followUpError, setFollowUpError] = useState<string | null>(null);

  const latestTargetId = lastInvestorMessage?.id ?? null;
  const latestResult = useMemo(
    () => history.find((entry) => entry.targetMessageId === latestTargetId) ?? null,
    [history, latestTargetId],
  );
  const isStale = Boolean(latestTargetId && !latestResult);
  const canInvoke = Boolean(conversationId) && Boolean(lastInvestorMessage) && !disabled;
  const panelVisible = mode !== 'closed';

  useEffect(() => {
    setMode('closed');
    setHistory([]);
    setExpandedTargetId(null);
    setError(null);
    setLoading(false);
    setFollowUpDraft('');
    setFollowUpLoadingId(null);
    setFollowUpError(null);
  }, [conversationId]);

  useEffect(() => {
    setFollowUpDraft('');
    setFollowUpError(null);
  }, [expandedTargetId]);

  useEffect(() => {
    if (mode === 'closed' || !expandedTargetId) return;
    const el = document.querySelector(`[data-chat-message-id="${expandedTargetId}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [mode, expandedTargetId]);

  const loadHistory = useCallback(async () => {
    if (!conversationId) return [];
    const entries = await FidesChatAdminService.listAiAssistHistory(conversationId);
    const sorted = sortHistoryChronological(entries);
    setHistory(sorted);
    return sorted;
  }, [conversationId]);

  const loadAssist = useCallback(
    async (force = false, targetMessageId?: string) => {
      if (!conversationId) return null;

      setLoading(true);
      setError(null);

      try {
        const data = await FidesChatAdminService.requestAiAssist(conversationId, {
          force,
          targetMessageId,
        });
        setHistory((current) => upsertHistoryEntry(current, data));
        setExpandedTargetId(data.targetMessageId);
        return data;
      } catch (assistError) {
        console.error('Failed to load AI assist', assistError);
        setError('Unable to generate Vera assist. Try again in a moment.');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [conversationId],
  );

  const openAssistForTarget = useCallback(
    async (targetMessageId: string) => {
      if (!conversationId) return;

      setMode('open');
      setExpandedTargetId(targetMessageId);

      const entries = await loadHistory();
      const existing = entries.find((entry) => entry.targetMessageId === targetMessageId);
      if (!existing) {
        await loadAssist(false, targetMessageId);
      }
    },
    [conversationId, loadAssist, loadHistory],
  );

  const openPanel = useCallback(async () => {
    if (!conversationId || !lastInvestorMessage) return;
    await openAssistForTarget(lastInvestorMessage.id);
  }, [conversationId, lastInvestorMessage, openAssistForTarget]);

  const submitFollowUp = useCallback(
    async (targetMessageId: string, question: string, options?: { followUpId?: string }) => {
      if (!conversationId) return null;

      const trimmed = question.trim();
      if (!trimmed && !options?.followUpId) return null;

      const loadingKey = options?.followUpId
        ? `${targetMessageId}:${options.followUpId}`
        : targetMessageId;
      setFollowUpLoadingId(loadingKey);
      setFollowUpError(null);

      try {
        const data = await FidesChatAdminService.requestAiAssistFollowUp(conversationId, {
          targetMessageId,
          question: trimmed,
          followUpId: options?.followUpId,
        });
        setHistory((current) => upsertHistoryEntry(current, data));
        if (!options?.followUpId) {
          setFollowUpDraft('');
        }
        return data;
      } catch (followUpErr) {
        console.error('Failed to submit Vera assist follow-up', followUpErr);
        setFollowUpError('Unable to get a follow-up response. Try again in a moment.');
        return null;
      } finally {
        setFollowUpLoadingId(null);
      }
    },
    [conversationId],
  );

  const regenerateFollowUp = useCallback(
    async (targetMessageId: string, followUpId: string, question: string) =>
      submitFollowUp(targetMessageId, question, { followUpId }),
    [submitFollowUp],
  );

  const targetPreview = useMemo(() => {
    const previewSource = latestResult ?? history[history.length - 1];
    if (!previewSource) return null;
    const trimmed = previewSource.targetMessageText.trim();
    return trimmed.length > 72 ? `${trimmed.slice(0, 71)}…` : trimmed;
  }, [history, latestResult]);

  return {
    mode,
    setMode,
    history,
    expandedTargetId,
    setExpandedTargetId,
    latestResult,
    loading,
    error,
    followUpDraft,
    setFollowUpDraft,
    followUpLoadingId,
    followUpError,
    submitFollowUp,
    isStale,
    canInvoke,
    panelVisible,
    targetPreview,
    openPanel,
    openAssistForTarget,
    loadAssist,
    regenerateFollowUp,
    targetMessageId: mode === 'closed' ? null : expandedTargetId,
    latestTargetId,
  };
}

export type ChatAiAssistState = ReturnType<typeof useChatAiAssist>;
